import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ApplicantType, EiaReferralSlaItem } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['ENV_OFFICER'];

/**
 * M17 (Planning Authority) ENVIRONMENT referral intake + the internal SLA
 * dashboard. M17 has no outbound webhook caller of its own (it didn't exist
 * when M17 was built), so the real-world seam is offline coordination: an
 * ENV_OFFICER told about the referral (phone/letter/email) logs it in here
 * via POST /eia-cases/intake-referral; once decided, the EIA case's own
 * "Overview" tab has the real "Respond to M17 referral" action
 * (POST /eia-cases/:id/respond-referral), which makes a genuine HTTP call
 * back into M17's live POST /api/v1/m17/referrals/:id/respond.
 */
export function Referrals() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ENV_OFFICER') ?? false;
  const [showIntake, setShowIntake] = useState(false);
  const slaQ = useApiQuery<{ generatedAt: string; count: number; items: EiaReferralSlaItem[] }>(
    isOfficer ? '/api/v1/m31/referrals/sla-dashboard' : null,
  );

  return (
    <div>
      <PageHeader
        title="M17 Planning Referrals"
        subtitle="EIA cases sourced from an M17 ENVIRONMENT referral, awaiting this module's response back to Planning."
        actions={isOfficer ? <button className="btn-primary" onClick={() => setShowIntake(true)}>+ Intake M17 referral</button> : undefined}
      />

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}

      {isOfficer ? (
        <>
          {slaQ.error ? <ErrorNote message={slaQ.error} /> : null}
          {slaQ.loading ? (
            <Spinner />
          ) : (slaQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No M17-sourced cases awaiting a referral response" />
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Planning ref</th>
                    <th className="table-head">State</th>
                    <th className="table-head">SLA due</th>
                    <th className="table-head">Deemed threshold</th>
                    <th className="table-head">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {slaQ.data!.items.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="table-cell font-mono text-xs font-medium">{c.refNo}</td>
                      <td className="table-cell text-xs text-slate-500">{c.projectRef}</td>
                      <td className="table-cell">
                        <StatusBadge status={c.state} />
                      </td>
                      <td className="table-cell text-xs">{formatDateTime(c.referralSlaDueAt)}</td>
                      <td className="table-cell text-xs">{formatDateTime(c.referralDeemedThresholdAt)}</td>
                      <td className="table-cell">
                        {c.slaOverdue ? <StatusBadge status="OVERDUE" /> : null}
                        {c.deemedOverdue ? <span className="ml-1 text-[11px] font-semibold text-rose-600">DEEMED</span> : null}
                        {!c.slaOverdue && !c.deemedOverdue ? <span className="text-xs text-slate-400">on track</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-3 text-xs text-slate-400">Generated {slaQ.data ? formatDateTime(slaQ.data.generatedAt) : '—'} · open a case from the EIA Cases screen to record its decision and respond to the referral.</p>
        </>
      ) : null}

      {showIntake ? (
        <IntakeModal
          onClose={() => setShowIntake(false)}
          onDone={() => {
            setShowIntake(false);
            slaQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function IntakeModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [planningRefNo, setPlanningRefNo] = useState('');
  const [planningReferralId, setPlanningReferralId] = useState('');
  const [parcelNo, setParcelNo] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectSector, setProjectSector] = useState('DEVELOPMENT');
  const [applicantType, setApplicantType] = useState<ApplicantType>('PERSON');
  const [applicantId, setApplicantId] = useState('');
  const [proposedFloorArea, setProposedFloorArea] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ refNo: string }>('/api/v1/m31/eia-cases/intake-referral', {
        planningRefNo,
        planningReferralId,
        parcelNo,
        projectDescription,
        projectSector,
        applicantId,
        applicantType,
        ...(proposedFloorArea ? { proposedFloorArea: Number.parseFloat(proposedFloorArea) } : {}),
      });
      setCreated(res.refNo);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to intake referral');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Intake M17 referral" onClose={created ? onDone : onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {created ? (
          <div className="space-y-3">
            <p className="text-sm text-seagreen-700">EIA case {created} created from this M17 referral.</p>
            <button className="btn-primary" onClick={onDone}>
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="M17 planning application ref">
                <input className="field-input" value={planningRefNo} onChange={(e) => setPlanningRefNo(e.target.value)} placeholder="e.g. PLAN/2026/0042" />
              </Field>
              <Field label="M17 Referral.id">
                <input className="field-input" value={planningReferralId} onChange={(e) => setPlanningReferralId(e.target.value)} placeholder="cuid of the M17 Referral row" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Parcel number">
                <input className="field-input" value={parcelNo} onChange={(e) => setParcelNo(e.target.value)} placeholder="C9 GeoServe parcel no" />
              </Field>
              <Field label="Proposed floor area (m²)">
                <input className="field-input" type="number" value={proposedFloorArea} onChange={(e) => setProposedFloorArea(e.target.value)} />
              </Field>
            </div>
            <Field label="Project description">
              <textarea className="field-input" rows={2} value={projectDescription} onChange={(e) => setProjectDescription(e.target.value)} />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Sector">
                <input className="field-input" value={projectSector} onChange={(e) => setProjectSector(e.target.value)} />
              </Field>
              <Field label="Applicant type">
                <select className="field-input" value={applicantType} onChange={(e) => setApplicantType(e.target.value as ApplicantType)}>
                  <option value="PERSON">Person</option>
                  <option value="ENTITY">Entity</option>
                </select>
              </Field>
              <Field label="Applicant id (NIN / business id)">
                <input className="field-input" value={applicantId} onChange={(e) => setApplicantId(e.target.value)} />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={busy || !planningRefNo || !planningReferralId || !parcelNo || projectDescription.trim().length < 3 || !applicantId}
                onClick={submit}
              >
                {busy ? 'Creating…' : 'Intake referral'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
